import { useState, useEffect } from 'react';
import {
  AlertTriangle,
  Clock,
  Shield,
  Target,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Monitor,
  User,
  File,
  Globe,
  Terminal,
  Database,
  FileText,
  Zap,
  Info,
  Copy,
  Check,
  Mail,
  Key,
  Server,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/Tabs';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { SeverityBadge } from '../ui/SeverityBadge';
import { getAlertDetail, buildProcessTree, type ProcessTreeNode } from '../../services/alerts';
import { formatDateTime, cn } from '../../lib/utils';
import type { Alert, AlertEvidence, AlertDetail as AlertDetailType } from '../../data/types';

interface AlertDetailProps {
  tenantId: string;
  alert: Alert;
  onClose?: () => void;
}

const evidenceIcons: Record<string, typeof Terminal> = {
  process: Terminal,
  file: File,
  user: User,
  device: Monitor,
  ip: Globe,
  url: Globe,
  mailbox: Mail,
  cloudapp: Database,
  azureresource: Server,
  oauthapp: Key,
  registrykey: Database,
  registryvalue: Database,
};

export function AlertDetailPanel({ tenantId, alert, onClose: _onClose }: AlertDetailProps) {
  const [detail, setDetail] = useState<AlertDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedProcesses, setExpandedProcesses] = useState<Set<number>>(new Set());

  useEffect(() => {
    async function fetchDetail() {
      setLoading(true);
      const result = await getAlertDetail(tenantId, alert.alertId);
      if (result.success && result.data) {
        setDetail(result.data);
        // Expand all processes by default
        const processIds = result.data.evidence
          .filter(e => e.evidenceType === 'process' && e.processId)
          .map(e => e.processId as number);
        setExpandedProcesses(new Set(processIds));
      }
      setLoading(false);
    }
    fetchDetail();
  }, [tenantId, alert.alertId]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-24 bg-bg-tertiary rounded-xl animate-pulse" />
        <div className="h-64 bg-bg-tertiary rounded-xl animate-pulse" />
      </div>
    );
  }

  if (!detail) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-text-primary mb-2">
            Failed to load alert details
          </h3>
          <p className="text-text-secondary">
            Unable to retrieve evidence and details for this alert.
          </p>
        </CardContent>
      </Card>
    );
  }

  const processTree = buildProcessTree(detail.evidence);
  const processEvidence = detail.evidence.filter(e => e.evidenceType === 'process');
  const nonProcessEvidence = detail.evidence.filter(e => e.evidenceType !== 'process');
  
  // Group non-process evidence by type
  const evidenceByType = nonProcessEvidence.reduce((acc, ev) => {
    const type = ev.evidenceType;
    if (!acc[type]) acc[type] = [];
    acc[type].push(ev);
    return acc;
  }, {} as Record<string, AlertEvidence[]>);

  return (
    <div className="space-y-6">
      {/* Enhanced Alert Header */}
      <div className={cn(
        'rounded-xl border p-6',
        alert.severity === 'critical' || alert.severity === 'high'
          ? 'bg-red-50/50 dark:bg-red-900/10 border-red-200 dark:border-red-800'
          : 'bg-bg-secondary border-border-default'
      )}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <SeverityBadge severity={alert.severity} />
              {alert.category && (
                <Badge variant="blue">{alert.category}</Badge>
              )}
              {alert.status && (
                <Badge variant={alert.status === 'resolved' ? 'green' : 'yellow'}>
                  {alert.status}
                </Badge>
              )}
              {alert.threatDisplayName && (
                <Badge variant="red">{alert.threatDisplayName}</Badge>
              )}
            </div>
            
            <h2 className="text-xl font-semibold text-text-primary mb-2">
              {alert.title}
            </h2>
            
            <div className="flex flex-wrap items-center gap-4 text-sm text-text-secondary">
              <span className="font-mono">{alert.alertId}</span>
              {alert.serviceSource && (
                <>
                  <span className="text-text-muted">|</span>
                  <span>Source: {alert.serviceSource}</span>
                </>
              )}
              {alert.detectionSource && (
                <>
                  <span className="text-text-muted">|</span>
                  <span>Detection: {alert.detectionSource}</span>
                </>
              )}
            </div>
          </div>
          
          {/* Open in Defender Button - Prominent */}
          {alert.alertWebUrl && (
            <a
              href={alert.alertWebUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="primary" size="sm">
                <ExternalLink className="w-4 h-4" />
                Open in Defender
              </Button>
            </a>
          )}
        </div>

        {/* Quick Stats Bar */}
        <div className="mt-4 pt-4 border-t border-border-default grid grid-cols-2 md:grid-cols-4 gap-4">
          <QuickStat 
            icon={<Clock className="w-4 h-4" />}
            label="Created"
            value={formatDateTime(alert.createdDateTime)}
          />
          {alert.firstActivityDateTime && (
            <QuickStat 
              icon={<Clock className="w-4 h-4" />}
              label="First Activity"
              value={formatDateTime(alert.firstActivityDateTime)}
            />
          )}
          <QuickStat 
            icon={<Database className="w-4 h-4" />}
            label="Evidence Items"
            value={`${detail.evidence.length}`}
          />
          {alert.mitreTechniques && alert.mitreTechniques.length > 0 && (
            <QuickStat 
              icon={<Target className="w-4 h-4" />}
              label="MITRE Techniques"
              value={`${alert.mitreTechniques.length}`}
            />
          )}
        </div>
      </div>

      {/* Tabbed Content */}
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" icon={<FileText className="w-4 h-4" />}>
            Overview
          </TabsTrigger>
          <TabsTrigger value="process-tree" icon={<Terminal className="w-4 h-4" />}>
            Process Tree ({processEvidence.length})
          </TabsTrigger>
          <TabsTrigger value="evidence" icon={<Database className="w-4 h-4" />}>
            Evidence ({nonProcessEvidence.length})
          </TabsTrigger>
          <TabsTrigger value="mitre" icon={<Target className="w-4 h-4" />}>
            MITRE ({alert.mitreTechniques?.length || 0})
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Description */}
            {alert.description && (
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Info className="w-5 h-5 text-accent-blue" />
                    Alert Description
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-text-secondary whitespace-pre-wrap leading-relaxed">
                    {alert.description}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Timeline */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-accent-blue" />
                  Alert Timeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <TimelineItem
                    label="First Activity"
                    time={alert.firstActivityDateTime}
                    color="blue"
                  />
                  <TimelineItem
                    label="Alert Created"
                    time={alert.createdDateTime}
                    color="green"
                  />
                  <TimelineItem
                    label="Last Activity"
                    time={alert.lastActivityDateTime}
                    color="amber"
                  />
                  {alert.resolvedDateTime && (
                    <TimelineItem
                      label="Resolved"
                      time={alert.resolvedDateTime}
                      color="slate"
                    />
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recommended Actions */}
            <Card className="border-green-200 dark:border-green-800 bg-green-50/30 dark:bg-green-900/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-400">
                  <Shield className="w-5 h-5" />
                  Recommended Actions
                </CardTitle>
              </CardHeader>
              <CardContent>
                {alert.recommendedActions && alert.recommendedActions.length > 0 ? (
                  <ul className="space-y-2">
                    {alert.recommendedActions.map((action, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-green-800 dark:text-green-200">
                        <Zap className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>{action}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-text-secondary text-sm">
                    No specific actions recommended
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Threat Info */}
            {(alert.threatDisplayName || alert.threatFamilyName) && (
              <Card className="border-red-200 dark:border-red-800 bg-red-50/30 dark:bg-red-900/10">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
                    <AlertTriangle className="w-5 h-5" />
                    Threat Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {alert.threatDisplayName && (
                      <div>
                        <span className="text-xs text-text-secondary uppercase tracking-wide">Threat Name</span>
                        <div className="font-medium text-red-700 dark:text-red-300">{alert.threatDisplayName}</div>
                      </div>
                    )}
                    {alert.threatFamilyName && (
                      <div>
                        <span className="text-xs text-text-secondary uppercase tracking-wide">Threat Family</span>
                        <div className="font-medium text-red-700 dark:text-red-300">{alert.threatFamilyName}</div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Process Tree Tab */}
        <TabsContent value="process-tree">
          {processTree.length > 0 ? (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Terminal className="w-5 h-5 text-purple-500" />
                  Process Execution Tree
                </CardTitle>
                <div className="flex items-center gap-2 text-xs text-text-secondary">
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-red-500" /> Suspicious/Malicious
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-slate-400" /> Normal
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-bg-tertiary rounded-lg p-4 font-mono text-sm overflow-x-auto">
                  {processTree.map((node, idx) => (
                    <ProcessTreeNodeComponent
                      key={idx}
                      node={node}
                      depth={0}
                      expanded={expandedProcesses}
                      onToggle={(pid) => {
                        const newSet = new Set(expandedProcesses);
                        if (newSet.has(pid)) {
                          newSet.delete(pid);
                        } else {
                          newSet.add(pid);
                        }
                        setExpandedProcesses(newSet);
                      }}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <Terminal className="w-12 h-12 text-text-muted mx-auto mb-4" />
                <h3 className="text-lg font-medium text-text-primary mb-2">
                  No Process Data
                </h3>
                <p className="text-text-secondary">
                  No process execution data is available for this alert.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Evidence Tab */}
        <TabsContent value="evidence">
          <div className="space-y-6">
            {alert.customDetails && (
              <DetectionDetailsCard customDetails={alert.customDetails} />
            )}
            {nonProcessEvidence.length > 0 ? (
              Object.entries(evidenceByType).map(([type, items]) => (
                <EvidenceSection key={type} type={type} items={items} />
              ))
            ) : !alert.customDetails ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Database className="w-12 h-12 text-text-muted mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-text-primary mb-2">
                    No Additional Evidence
                  </h3>
                  <p className="text-text-secondary">
                    No non-process evidence items found for this alert.
                  </p>
                </CardContent>
              </Card>
            ) : null}
          </div>
        </TabsContent>

        {/* MITRE Tab */}
        <TabsContent value="mitre">
          {alert.mitreTechniques && alert.mitreTechniques.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-orange-500" />
                  MITRE ATT&CK Techniques
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {alert.mitreTechniques.map((technique, idx) => (
                    <MitreTechniqueCard key={idx} technique={technique} />
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <Target className="w-12 h-12 text-text-muted mx-auto mb-4" />
                <h3 className="text-lg font-medium text-text-primary mb-2">
                  No MITRE Techniques
                </h3>
                <p className="text-text-secondary">
                  No MITRE ATT&CK techniques have been associated with this alert.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

function QuickStat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="text-text-muted">{icon}</div>
      <div>
        <div className="text-xs text-text-secondary">{label}</div>
        <div className="text-sm font-medium text-text-primary">{value}</div>
      </div>
    </div>
  );
}

function TimelineItem({ label, time, color }: { label: string; time: string | null; color: string }) {
  if (!time) return null;
  
  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    amber: 'bg-amber-500',
    red: 'bg-red-500',
    slate: 'bg-slate-500',
  };

  return (
    <div className="flex items-center gap-3">
      <div className={cn('w-2 h-2 rounded-full', colorClasses[color] || 'bg-slate-500')} />
      <div className="flex-1">
        <div className="text-xs text-text-secondary">{label}</div>
        <div className="text-sm font-medium text-text-primary">
          {formatDateTime(time)}
        </div>
      </div>
    </div>
  );
}

/**
 * Renders analytic-rule custom details (e.g. full PowerShell command sets)
 * captured on the alert. Values are shown unabridged in monospace with copy.
 */
export function DetectionDetailsCard({
  customDetails,
  title,
}: {
  customDetails: Record<string, string[]>;
  title?: string;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Terminal className="w-5 h-5 text-purple-500" />
          {title ?? 'Detection Details'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {Object.entries(customDetails).map(([name, values]) => (
          <div key={name}>
            <p className="text-xs font-medium text-text-secondary uppercase tracking-wide mb-2">
              {name}
            </p>
            <div className="space-y-2">
              {values.map((value, idx) => (
                <DetectionDetailValue key={idx} value={value} />
              ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function DetectionDetailValue({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-start justify-between gap-2 p-3 rounded-lg bg-bg-tertiary">
      <code className="font-mono text-sm text-text-primary whitespace-pre-wrap break-all flex-1">
        {value}
      </code>
      <button
        onClick={handleCopy}
        className="p-1 text-text-muted hover:text-text-secondary transition-colors shrink-0"
        title="Copy value"
      >
        {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
}

function MitreTechniqueCard({ technique }: { technique: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(technique);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Parse technique ID if present (e.g., "T1059.001")
  const isId = /^T\d{4}/.test(technique);
  const attackUrl = isId ? `https://attack.mitre.org/techniques/${technique.replace('.', '/')}/` : null;

  return (
    <div className="flex items-center justify-between p-3 rounded-lg border border-orange-200 dark:border-orange-800 bg-orange-50/50 dark:bg-orange-900/10">
      <div className="flex items-center gap-2">
        <Target className="w-4 h-4 text-orange-500" />
        <span className="font-mono text-sm text-orange-700 dark:text-orange-300">{technique}</span>
      </div>
      <div className="flex items-center gap-1">
        {attackUrl && (
          <a
            href={attackUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1 text-text-muted hover:text-accent-blue transition-colors"
            title="View on MITRE ATT&CK"
          >
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
        <button
          onClick={handleCopy}
          className="p-1 text-text-muted hover:text-text-secondary transition-colors"
          title="Copy technique ID"
        >
          {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

function EvidenceSection({ type, items }: { type: string; items: AlertEvidence[] }) {
  const Icon = evidenceIcons[type] || Database;
  const typeLabels: Record<string, string> = {
    device: 'Devices',
    user: 'Users',
    file: 'Files',
    ip: 'IP Addresses',
    url: 'URLs',
    mailbox: 'Mailboxes',
    cloudapp: 'Cloud Applications',
    azureresource: 'Azure Resources',
    oauthapp: 'OAuth Applications',
    registrykey: 'Registry Keys',
    registryvalue: 'Registry Values',
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-accent-blue" />
          {typeLabels[type] || type} ({items.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {items.map((ev) => (
            <EvidenceItem key={ev.evidenceId} evidence={ev} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

interface ProcessTreeNodeProps {
  node: ProcessTreeNode;
  depth: number;
  expanded: Set<number>;
  onToggle: (pid: number) => void;
}

function ProcessTreeNodeComponent({ node, depth, expanded, onToggle }: ProcessTreeNodeProps) {
  const { evidence, children } = node;
  const pid = evidence.processId;
  const isExpanded = pid ? expanded.has(pid) : true;
  const hasChildren = children.length > 0;

  const fileName = evidence.imageFile?.split(/[/\\]/).pop() || evidence.displayName || 'Unknown';
  const isSuspicious = evidence.verdict === 'suspicious' || evidence.verdict === 'malicious';

  return (
    <div style={{ marginLeft: depth * 20 }}>
      <div
        className={cn(
          'flex items-center gap-2 py-2 px-3 rounded-lg mb-1 transition-colors',
          isSuspicious 
            ? 'bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800' 
            : 'hover:bg-bg-tertiary'
        )}
      >
        {hasChildren ? (
          <button
            onClick={() => pid && onToggle(pid)}
            className="p-1 hover:bg-bg-tertiary rounded transition-colors"
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-text-secondary" />
            ) : (
              <ChevronRight className="w-4 h-4 text-text-secondary" />
            )}
          </button>
        ) : (
          <span className="w-6" />
        )}
        
        <div className={cn(
          'p-1.5 rounded',
          isSuspicious ? 'bg-red-200 dark:bg-red-800' : 'bg-bg-tertiary'
        )}>
          <Terminal className={cn('w-4 h-4', isSuspicious ? 'text-red-600 dark:text-red-400' : 'text-text-secondary')} />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn(
              'font-medium truncate',
              isSuspicious ? 'text-red-700 dark:text-red-300' : 'text-text-primary'
            )}>
              {fileName}
            </span>
            {pid && (
              <span className="text-xs text-text-muted">
                PID: {pid}
              </span>
            )}
            {isSuspicious && (
              <Badge variant="red" className="text-xs">
                {evidence.verdict}
              </Badge>
            )}
          </div>
          {evidence.processCommandLine && (
            <div
              className="text-xs text-text-secondary truncate mt-0.5 max-w-2xl" 
              title={evidence.processCommandLine}
            >
              {evidence.processCommandLine}
            </div>
          )}
        </div>
      </div>
      
      {isExpanded && children.map((child, idx) => (
        <ProcessTreeNodeComponent
          key={idx}
          node={child}
          depth={depth + 1}
          expanded={expanded}
          onToggle={onToggle}
        />
      ))}
    </div>
  );
}

interface EvidenceItemProps {
  evidence: AlertEvidence;
}

function EvidenceItem({ evidence }: EvidenceItemProps) {
  const [expanded, setExpanded] = useState(false);
  const Icon = evidenceIcons[evidence.evidenceType] || Database;
  const isSuspicious = evidence.verdict === 'suspicious' || evidence.verdict === 'malicious';

  return (
    <div 
      className={cn(
        'rounded-lg border cursor-pointer transition-colors',
        isSuspicious
          ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
          : 'border-border-default bg-bg-tertiary/50 hover:border-border-default'
      )}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex items-start gap-3 p-3">
        <div className={cn(
          'p-2 rounded',
          isSuspicious ? 'bg-red-100 dark:bg-red-800' : 'bg-bg-tertiary'
        )}>
          <Icon className={cn('w-4 h-4', isSuspicious ? 'text-red-500' : 'text-text-secondary')} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-text-primary truncate">
              {evidence.displayName || 'Unknown'}
            </span>
            {evidence.verdict && (
              <Badge variant={isSuspicious ? 'red' : 'green'} className="text-xs">
                {evidence.verdict}
              </Badge>
            )}
            {evidence.remediationStatus && (
              <Badge variant="blue" className="text-xs">
                {evidence.remediationStatus}
              </Badge>
            )}
          </div>
          
          {/* Quick preview details */}
          <div className="mt-1 text-xs text-text-secondary">
            {evidence.evidenceType === 'device' && evidence.deviceDnsName && (
              <span>{evidence.deviceDnsName}</span>
            )}
            {evidence.evidenceType === 'user' && evidence.userPrincipalName && (
              <span>{evidence.userPrincipalName}</span>
            )}
            {evidence.evidenceType === 'ip' && evidence.ipAddress && (
              <span>{evidence.ipAddress}</span>
            )}
            {evidence.evidenceType === 'file' && evidence.filePath && (
              <span className="truncate block">{evidence.filePath}</span>
            )}
            {evidence.evidenceType === 'url' && evidence.url && (
              <span className="truncate block">{evidence.url}</span>
            )}
          </div>
        </div>
        <ChevronDown className={cn(
          'w-4 h-4 text-text-muted transition-transform',
          expanded && 'rotate-180'
        )} />
      </div>
      
      {/* Expanded details */}
      {expanded && (
        <div className="px-3 pb-3 border-t border-border-default mt-2 pt-3">
          <div className="grid grid-cols-2 gap-2 text-xs">
            {evidence.deviceDnsName && (
              <DetailRow label="Device DNS" value={evidence.deviceDnsName} />
            )}
            {evidence.osPlatform && (
              <DetailRow label="OS Platform" value={evidence.osPlatform} />
            )}
            {evidence.osVersion && (
              <DetailRow label="OS Version" value={evidence.osVersion} />
            )}
            {evidence.userPrincipalName && (
              <DetailRow label="UPN" value={evidence.userPrincipalName} />
            )}
            {evidence.accountName && (
              <DetailRow label="Account" value={evidence.accountName} />
            )}
            {evidence.domainName && (
              <DetailRow label="Domain" value={evidence.domainName} />
            )}
            {evidence.filePath && (
              <DetailRow label="File Path" value={evidence.filePath} span />
            )}
            {evidence.sha256 && (
              <DetailRow label="SHA256" value={evidence.sha256} mono span />
            )}
            {evidence.sha1 && (
              <DetailRow label="SHA1" value={evidence.sha1} mono />
            )}
            {evidence.fileSize && (
              <DetailRow label="File Size" value={`${evidence.fileSize.toLocaleString()} bytes`} />
            )}
            {evidence.ipAddress && (
              <DetailRow label="IP Address" value={evidence.ipAddress} mono />
            )}
            {evidence.url && (
              <DetailRow label="URL" value={evidence.url} span />
            )}
            {evidence.mdeDeviceId && (
              <DetailRow label="MDE Device ID" value={evidence.mdeDeviceId} mono />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value, mono = false, span = false }: { 
  label: string; 
  value: string; 
  mono?: boolean;
  span?: boolean;
}) {
  return (
    <div className={cn(span && 'col-span-2')}>
      <span className="text-text-secondary">{label}:</span>{' '}
      <span className={cn(
        'text-text-secondary',
        mono && 'font-mono',
        span && 'break-all'
      )}>
        {value}
      </span>
    </div>
  );
}

export default AlertDetailPanel;
